import { ActivatedRouteSnapshot, Resolve, Router } from '@angular/router';
import { getEvent, getActivePage } from '@yoimo/client-sdk/events';
import {
  AccessControlService,
  AuthService,
  EventsService,
  MetaService,
  PromotionService,
  ScopeService,
} from '@core/services';
import {
  Observable,
  catchError,
  map,
  of,
  switchMap,
  combineLatest,
} from 'rxjs';

import { Firestore } from '@angular/fire/firestore';
import { Injectable } from '@angular/core';
import { BrandingService } from '@yoimo/joymo-ui';
import { getPageHeader } from '@yoimo/client-sdk/pages';
import { PageScope } from 'shared-lib/core';
import { mapPageToUIPage } from '@core/utilities/pages';

@Injectable({ providedIn: 'root' })
export class EventPageResolver implements Resolve<PageScope | undefined> {
  constructor(
    private fs: Firestore,
    private router: Router,
    private metaService: MetaService,
    private scopeService: ScopeService,
    private eventService: EventsService,
    private brandingService: BrandingService,
    private accessControlService: AccessControlService,
    private promotionService: PromotionService,
    private authService: AuthService
  ) {}

  resolve(route: ActivatedRouteSnapshot): Observable<PageScope | undefined> {
    const eventId = route.paramMap.get('eventId');
    if (!eventId) {
      console.error('eventId parameter not found', route.paramMap);
      return of(undefined);
    }

    return combineLatest([
      this.scopeService.scope$,
      this.scopeService.listenToChannel(),
    ]).pipe(
      switchMap(([scope, channel]) => {
        return getEvent(this.fs, scope.scopeId, eventId).pipe(
          map((event) => ({ channel, event }))
        );
      }),
      map(({ channel, event }) => {
        if (!event) {
          throw new Error('Event not found or internal error');
        }

        let eventActivePage = getActivePage(event);
        if (!eventActivePage) {
          throw new Error('No active page for event');
        }

        this.metaService.setTags({
          title: event.title,
          description: event.description,
          image: event.posterImage,
        });

        if (eventActivePage.metaTags) {
          this.metaService.setRawTags(eventActivePage.metaTags);
        }

        if (eventActivePage.brandingOverride) {
          const branding = this.eventService.getEventBranding(channel, event);
          this.brandingService.setOverride(branding, true);
        }

        const header = getPageHeader(eventActivePage, { channel, event });
        const defaultPurchaseOptions$ = event.hideTicketCTA
          ? of([])
          : this.eventService.getApplicableProducts$(channel.docId, event);

        return {
          channel,
          event,
          activePage: mapPageToUIPage(
            eventActivePage,
            header,
            'DEFAULT',
            route.queryParams['tabId']
          ),
          userHasAccess$: this.authService.user$.pipe(
            switchMap((user) =>
              this.accessControlService.userHasEventAccess$(
                user,
                channel,
                event
              )
            )
          ),
          promotionalBlock$: this.promotionService.getPromotionalBlock$(
            header,
            channel,
            defaultPurchaseOptions$
          ),
          pageDescription: {
            title: event.title,
            description: event.description,
          },
        };
      }),
      catchError((err, caught) => {
        console.error(err);
        this.router.navigate(['/404'], { skipLocationChange: true });
        return of(undefined);
      })
    );
  }
}
